import React, { useState } from 'react';
import { Alert, Button, Card, Space, Typography, Upload, message, Progress, List } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { BATCH_IMPORT_ACCESS_CODE, BatchImportAccessCodeResponse, BatchImportAccessCodeInput } from './graphql/batchImportAccessCode';
import { fileUpload } from 'utils/storageUtils';
import type { UploadFile } from 'antd';

const { Title, Paragraph, Text } = Typography;

const AccessCodeImportPage: React.FC = () => {
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [result, setResult] = useState<BatchImportAccessCodeResponse['batchImportAccessCode'] | null>(null);

    const [batchImportAccessCode, { loading }] = useMutation<
        BatchImportAccessCodeResponse,
        { input: BatchImportAccessCodeInput }
    >(BATCH_IMPORT_ACCESS_CODE);

    const handleUpload = async (file: File) => {
        if (!file.name.toLowerCase().endsWith('.csv')) {
            message.error('只能上传CSV文件（.csv）');
            return false;
        }

        // 检查文件大小（100MB限制）
        const isLt100M = file.size / 1024 / 1024 < 100;
        if (!isLt100M) {
            message.error('文件大小不能超过100MB');
            return false;
        }

        setUploading(true);
        setUploadProgress(0);
        setResult(null);

        try {
            console.log('开始文件上传流程');
            console.log('文件信息:', {
                name: file.name,
                size: file.size,
                type: file.type
            });

            // 步骤1: 上传文件到存储
            console.log('步骤1: 上传文件到存储');
            setUploadProgress(30);
            
            const { objectKey } = await fileUpload(file, 'access-code-import');
            console.log('文件上传成功，objectKey:', objectKey);

            // 步骤2: 调用GraphQL mutation处理文件
            console.log('步骤2: 触发后端处理');
            setUploadProgress(60);
            
            const { data } = await batchImportAccessCode({
                variables: {
                    input: {
                        objectKey: objectKey,
                    },
                },
            });

            if (data?.batchImportAccessCode) {
                setResult(data.batchImportAccessCode);
                setUploadProgress(100);
                message.success('批量导入完成！');
            }
        } catch (error: any) {
            console.error('文件处理失败:', error);
            message.error(`文件处理失败: ${error.message}`);
        } finally {
            // 无论成功还是失败，都要关闭 loading
            setUploading(false);
        }
        
        return false; // 阻止默认上传行为
    };

    const uploadProps = {
        beforeUpload: (file: File) => {
            handleUpload(file);
            return false; // 阻止自动上传
        },
        fileList,
        onRemove: () => {
            setFileList([]);
            setResult(null);
        },
        accept: '.csv',
        maxCount: 1,
    };

    const failureDetails = result?.failedDetails || [];

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <Card>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Title level={2}>门禁码批量导入工具</Title>
                        <Paragraph type="secondary">
                            上传CSV文件批量导入门禁码。文件第一行为表头，支持按表头名称读取数据。
                        </Paragraph>
                        <Paragraph type="secondary">
                            <Text strong>支持的字段名称：</Text> street, unit, city, state, postal_code, mapped_type, code, status
                        </Paragraph>
                        <Paragraph type="secondary">
                            <Text strong>限制：</Text> 文件大小不超过100MB，最大行数不超过100000行
                        </Paragraph>
                    </div>

                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined />} loading={uploading || loading}>
                                选择CSV文件
                            </Button>
                        </Upload>

                        {uploading && (
                            <Progress percent={uploadProgress} status="active" />
                        )}

                        {result && (
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <Alert
                                    message={
                                        <Space direction="vertical" size={0}>
                                            <Text>总计：{result.totalCount}</Text>
                                            <Text type="success">成功：{result.successCount}</Text>
                                            <Text type="danger">失败：{result.failedCount}</Text>
                                        </Space>
                                    }
                                    type="info"
                                    showIcon
                                />

                                {failureDetails.length > 0 && (
                                    <Alert
                                        type="warning"
                                        message="失败详情"
                                        description={
                                            <List
                                                size="small"
                                                dataSource={failureDetails}
                                                renderItem={(item) => (
                                                    <List.Item>
                                                        <Space direction="vertical" style={{ width: '100%' }}>
                                                            <Text strong>第 {item.lineNumber} 行</Text>
                                                            {item.address && (
                                                                <Text>地址：{item.address}</Text>
                                                            )}
                                                            <Text type="danger">{item.errorMessage}</Text>
                                                        </Space>
                                                    </List.Item>
                                                )}
                                            />
                                        }
                                        showIcon
                                    />
                                )}
                            </Space>
                        )}
                    </Space>
                </Space>
            </Card>
        </div>
    );
};

export default AccessCodeImportPage;

